import React, { useEffect, useRef, useState } from "react";
import { FiSend, FiRotateCcw, FiThumbsUp, FiThumbsDown, FiUserCheck, FiBookOpen, FiCheckCircle } from "react-icons/fi";
import Answer from "../chatbot/Answer";
import { PRIORITY_LABELS, PRIORITY_STYLES, SAMPLE_COMPANY, SAMPLE_KNOWLEDGE, SAMPLE_QUESTIONS, SANDBOX_MAX_CHARS, askSandbox, historyOf, knowledgeStats, ticketPreview } from "../../chatbot/support";

const field = "w-full bg-navy border border-navy-border rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-light";

// The person is needed: the customer leaves contact details and a ticket is made. (Here nothing is stored: the card shows what would arrive in the team's inbox.)
function Handoff({ messages, priority }) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [ticket, setTicket] = useState(null);
  const [problem, setProblem] = useState("");
  const create = () => {
    if (!contact.trim()) { setProblem("Please give an email address or a phone number."); return; }
    setProblem("");
    setTicket(ticketPreview({ messages, name: name.trim(), contact: contact.trim(), priority }));
  };
  if (ticket) {
    return (
      <div className="mt-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs" role="status">
        <div className="flex items-center gap-2 font-semibold text-emerald-200 mb-1.5"><FiCheckCircle /> Ticket created (example)<span className={`ml-auto px-2 py-0.5 rounded-full ${PRIORITY_STYLES[ticket.priority]}`}>{PRIORITY_LABELS[ticket.priority]}</span></div>
        <div><b>{ticket.subject}</b></div>
        <div className="text-gray-300">{ticket.name} - {ticket.contact}</div>
        <pre className="mt-1.5 whitespace-pre-wrap font-sans text-gray-400 max-h-28 overflow-y-auto">{ticket.summary}</pre>
        <p className="mt-1.5 text-gray-500">In your assistant this lands in your ticket inbox, with the whole chat, and the customer gets a reference number.</p>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2">
      <div className="flex items-center gap-2 font-semibold text-amber-100"><FiUserCheck /> This needs a team member<span className={`ml-auto px-2 py-0.5 rounded-full ${PRIORITY_STYLES[priority || "normal"]}`}>{PRIORITY_LABELS[priority || "normal"]} priority</span></div>
      <input className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" aria-label="Your name" maxLength={100} />
      <input className={field} value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Email or phone number" aria-label="Email or phone number" maxLength={120} />
      {problem && <div className="text-red-300" role="alert">{problem}</div>}
      <button type="button" onClick={create} className="px-3 py-1.5 rounded-lg bg-brand-gradient font-semibold">Send to the team</button>
    </div>
  );
}

// The try-it box: the visitor writes (or loads) their company's knowledge and talks to a support assistant that knows only that. Answers stream in, show their source, can be rated, and hand over to a person when needed.
export default function SupportSandbox() {
  const [company, setCompany] = useState(SAMPLE_COMPANY);
  const [knowledge, setKnowledge] = useState(SAMPLE_KNOWLEDGE);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const abort = useRef(null);
  const logRef = useRef(null);
  const stats = knowledgeStats(knowledge);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [messages]);
  useEffect(() => () => { if (abort.current) abort.current.abort(); }, []);

  const send = async (text) => {
    const question = String(text || "").trim();
    if (!question || busy || stats.over) return;
    setBusy(true);
    const id = Date.now();
    const history = historyOf(messages);
    setMessages((m) => [...m, { id, role: "user", text: question }, { id: id + 1, role: "assistant", text: "", pending: true }]);
    const patch = (change) => setMessages((m) => m.map((x) => (x.id === id + 1 ? { ...x, ...change } : x)));
    const controller = new AbortController();
    abort.current = controller;
    let reply = "";
    try {
      await askSandbox({
        company, knowledge, message: question, history, signal: controller.signal,
        onEvent: (e) => {
          if (e.delta) { reply += e.delta; patch({ text: reply, pending: false }); }
          if (e.done) patch({ text: e.reply, pending: false, handoff: e.handoff, sources: e.sources || [], priority: e.priority });
          if (e.error) patch({ text: e.error.message, pending: false, error: true });
        },
      });
    } catch (err) {
      if (!err || err.name !== "AbortError") patch({ text: "Something went wrong. Please try again.", pending: false, error: true });
    } finally {
      setBusy(false);
    }
  };

  const rate = (id, value) => setMessages((m) => m.map((x) => (x.id === id ? { ...x, rating: x.rating === value ? 0 : value } : x)));
  const reset = () => { if (abort.current) abort.current.abort(); setMessages([]); setBusy(false); };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div className="rounded-2xl bg-white/5 border border-navy-border p-5 flex flex-col">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><FiBookOpen /> 1. What your assistant knows</h3>
        <p className="text-xs text-gray-400 mb-3">Paste your FAQs, policies or product facts. Start a topic with a <code># Title</code> line. In the real assistant you also upload PDF or Word files and import web pages.</p>
        <input className={`${field} mb-3`} value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" aria-label="Company name" maxLength={80} />
        <textarea className={`${field} h-[420px] font-mono text-[12.5px] leading-relaxed`} value={knowledge} onChange={(e) => setKnowledge(e.target.value)} aria-label="Knowledge" spellCheck={false} />
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className={stats.over ? "text-red-300" : "text-gray-500"}>{stats.chars.toLocaleString()} / {SANDBOX_MAX_CHARS.toLocaleString()} characters - {stats.sections} topics</span>
          <span className="flex gap-2">
            <button type="button" className="underline text-gray-300 hover:text-white" onClick={() => { setCompany(SAMPLE_COMPANY); setKnowledge(SAMPLE_KNOWLEDGE); reset(); }}>Load the example</button>
            <button type="button" className="underline text-gray-300 hover:text-white" onClick={() => { setKnowledge(""); reset(); }}>Clear</button>
          </span>
        </div>
      </div>

      <div className="rounded-2xl bg-white/5 border border-navy-border flex flex-col h-[620px] max-h-[88vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-navy-border">
          <div><div className="font-semibold">{company || "Support Assistant"}</div><div className="text-[11px] text-gray-400">2. Ask it like a customer would</div></div>
          <button type="button" onClick={reset} title="Start over" aria-label="Start over" className="p-2 rounded-lg border border-navy-border text-gray-300 hover:border-accent-light"><FiRotateCcw /></button>
        </div>
        <div ref={logRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" role="log" aria-live="polite">
          <div className="max-w-[88%] rounded-2xl rounded-bl-sm bg-white/10 px-4 py-2.5 text-sm">Hi! I am the support assistant of {company || "this company"}. How can I help?</div>
          {!messages.length && (
            <div className="flex flex-wrap gap-2">
              {SAMPLE_QUESTIONS.map((q) => <button key={q} type="button" disabled={busy || stats.over} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full border border-navy-border text-gray-300 hover:border-accent-light disabled:opacity-40">{q}</button>)}
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={m.role === "user" ? "flex justify-end" : ""}>
              <div className={`max-w-[88%] ${m.role === "user" ? "" : "w-full"}`}>
                <div className={`px-4 py-2.5 text-sm leading-relaxed inline-block max-w-full ${m.role === "user" ? "rounded-2xl rounded-br-sm bg-brand-gradient" : m.error ? "rounded-2xl rounded-bl-sm bg-red-500/15 border border-red-500/30 text-red-100" : "rounded-2xl rounded-bl-sm bg-white/10"}`}>
                  {m.pending && !m.text ? <span className="inline-flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: `${i * 0.18}s` }} />)}</span> : <Answer text={m.text} />}
                </div>
                {m.role === "assistant" && m.sources && m.sources.length > 0 && <div className="mt-1 text-[11px] text-gray-400">From: {m.sources.map((s) => s.title).join(", ")}</div>}
                {m.role === "assistant" && m.priority !== undefined && !m.error && (
                  <div className="mt-1 flex gap-2">
                    <button type="button" onClick={() => rate(m.id, 1)} aria-pressed={m.rating === 1} aria-label="This answer helped" className={`p-1.5 rounded-md border text-xs ${m.rating === 1 ? "bg-emerald-500/30 border-emerald-400" : "border-navy-border text-gray-400 hover:border-accent-light"}`}><FiThumbsUp /></button>
                    <button type="button" onClick={() => rate(m.id, -1)} aria-pressed={m.rating === -1} aria-label="This answer did not help" className={`p-1.5 rounded-md border text-xs ${m.rating === -1 ? "bg-red-500/30 border-red-400" : "border-navy-border text-gray-400 hover:border-accent-light"}`}><FiThumbsDown /></button>
                  </div>
                )}
                {m.handoff && <Handoff messages={messages.slice(0, messages.indexOf(m) + 1)} priority={m.priority} />}
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); const t = input; setInput(""); send(t); }} className="p-3 border-t border-navy-border flex items-end gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} dir="auto" maxLength={4000} aria-label="Customer question" placeholder="Type a customer question..."
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); const t = input; setInput(""); send(t); } }}
            className="flex-1 resize-none bg-navy border border-navy-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-accent-light max-h-28" />
          <button type="submit" disabled={busy || !input.trim() || stats.over} aria-label="Send" className="p-2.5 rounded-xl bg-brand-gradient text-white disabled:opacity-40"><FiSend /></button>
        </form>
        <div className="px-4 pb-2.5 text-[11px] text-gray-500">Try the last question: asking for a person creates a ticket. AI answers can be wrong - check important facts.</div>
      </div>
    </div>
  );
}
